import React, { useCallback, useEffect, useState, useRef } from 'react';
import { XIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getTeacherStudents } from '../../utils/teacherUtils';
import { formatPhoneNumber, normalizePhoneNumber } from '../../utils/phoneUtils';
interface Student {
  id: string;
  name: string;
  avatar?: string;
  email?: string;
}
interface AddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  darkMode: boolean;
  onSuccess: (newStudent?: any) => void;
}
export const AddStudentModal: React.FC<AddStudentModalProps> = ({
  isOpen,
  onClose,
  student,
  darkMode,
  onSuccess
}) => {
  const {
    currentUser
  } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: student?.email || '',
    notes: '',
    phone: '',
    instrument: '',
    grade: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  // Validation error states
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [gradeError, setGradeError] = useState('');
  // Add drag state - OPTIMIZED: Use refs for drag offset to avoid re-renders
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({
    x: 0,
    y: 0
  });
  const rafIdRef = useRef<number | null>(null);
  const [dragStartPos, setDragStartPos] = useState({
    x: 0,
    y: 0
  });
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0
  });
  const modalRef = useRef<HTMLDivElement>(null);
  // Ref for phone input to manage cursor position
  const phoneInputRef = useRef<HTMLInputElement>(null);
  // Initialize modal position to center on mount
  useEffect(() => {
    if (isOpen && modalRef.current && window.innerWidth >= 768) {
      const modalWidth = 640; // max-w-xl = 40rem = 640px
      const modalHeight = modalRef.current.offsetHeight || 600;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = (viewportWidth - modalWidth) / 2;
      const top = (viewportHeight - modalHeight) / 2;
      setModalPosition({
        top,
        left
      });
      // Reset drag offset on every open
      dragOffsetRef.current = {
        x: 0,
        y: 0
      };
    }
  }, [isOpen]);
  // Add drag handlers - OPTIMIZED: Use transform + RAF like AddTaskModal
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Disable drag on mobile
    if (window.innerWidth < 768) return;
    if (!modalRef.current) return;
    // Don't start drag if clicking on close button
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
  }, []);
  const handleDrag = useCallback((e: MouseEvent) => {
    if (!modalRef.current) return;
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    dragOffsetRef.current = {
      x: deltaX,
      y: deltaY
    };
    // Cancel previous RAF if still pending
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    // Schedule DOM update on next frame
    rafIdRef.current = requestAnimationFrame(() => {
      if (modalRef.current) {
        const finalTop = modalPosition.top + dragOffsetRef.current.y;
        const finalLeft = modalPosition.left + dragOffsetRef.current.x;
        modalRef.current.style.transform = `translate(${finalLeft - modalPosition.left}px, ${finalTop - modalPosition.top}px)`;
      }
    });
  }, [dragStartPos, modalPosition]);
  const handleDragEnd = useCallback(() => {
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Calculate new position with bounds checking
    const modalWidth = modalRef.current?.offsetWidth || 640;
    const modalHeight = modalRef.current?.offsetHeight || 600;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let newLeft = modalPosition.left + dragOffsetRef.current.x;
    let newTop = modalPosition.top + dragOffsetRef.current.y;
    // Constrain to viewport bounds
    newLeft = Math.max(0, Math.min(newLeft, viewportWidth - modalWidth));
    newTop = Math.max(0, Math.min(newTop, viewportHeight - modalHeight));
    // Apply final position to DOM synchronously BEFORE clearing transform
    if (modalRef.current) {
      modalRef.current.style.top = `${newTop}px`;
      modalRef.current.style.left = `${newLeft}px`;
      modalRef.current.style.transform = '';
    }
    // Update React state to match DOM
    setModalPosition({
      top: newTop,
      left: newLeft
    });
    setIsDragging(false);
    dragOffsetRef.current = {
      x: 0,
      y: 0
    };
  }, [modalPosition]);
  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        // Cleanup RAF on unmount
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);
  // Prevent text selection while dragging
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Email validation function
  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setEmailError('Email is required');
      return false;
    }
    // Check for spaces
    if (email.includes(' ')) {
      setEmailError('Email cannot contain spaces');
      return false;
    }
    // Basic email validation: must have @ and text before/after, and at least one . after @
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address (example: name@example.com)');
      return false;
    }
    setEmailError('');
    return true;
  };
  // Phone validation function - UPDATED: Optional
  const validatePhone = (phone: string): boolean => {
    // Phone is now optional - if empty, it's valid
    if (!phone.trim()) {
      setPhoneError('');
      return true;
    }
    // Remove all formatting characters to count digits
    const digitsOnly = phone.replace(/[^\d]/g, '');
    // Check if phone contains only allowed characters
    const allowedCharsRegex = /^[\d\s\-\+\(\)]+$/;
    if (!allowedCharsRegex.test(phone)) {
      setPhoneError('Phone number can only contain digits, spaces, +, -, ( and )');
      return false;
    }
    // Require 8-15 digits for international numbers
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      setPhoneError('Please enter a valid phone number with country code');
      return false;
    }
    setPhoneError('');
    return true;
  };
  // Grade validation function - OPTIONAL field
  const validateGrade = (grade: string): boolean => {
    // Grade is optional - if empty, it's valid
    if (!grade.trim()) {
      setGradeError('');
      return true;
    }
    // Check if it's a valid number
    const gradeNum = parseInt(grade);
    if (isNaN(gradeNum)) {
      setGradeError('Grade must be a number');
      return false;
    }
    // Check if it's between 1 and 12
    if (gradeNum < 1 || gradeNum > 12) {
      setGradeError('Grade must be between 1 and 12');
      return false;
    }
    setGradeError('');
    return true;
  };
  // PHASE 3 ENHANCEMENT: Check for duplicate email/phone in addition to name
  const checkDuplicateStudent = async (firstName: string, lastName: string, email: string, phone: string): Promise<boolean> => {
    if (!currentUser?.uid) {
      return true; // Allow if no user (shouldn't happen)
    }
    try {
      // Combine first and last name
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const normalizedName = fullName.toLowerCase().trim();
      // Normalize email and phone for comparison
      const normalizedEmail = email.toLowerCase().trim();
      const normalizedPhone = normalizePhoneNumber(phone); // digits only
      console.log('[DUPLICATE CHECK] Checking for duplicate student:', {
        fullName,
        normalizedName,
        normalizedEmail,
        normalizedPhone,
        teacherId: currentUser.uid
      });
      // Get all existing students for this teacher
      const existingStudents = await getTeacherStudents(currentUser.uid);
      console.log('[DUPLICATE CHECK] Existing students:', {
        count: existingStudents.length,
        students: existingStudents.map(s => ({
          id: s.id,
          name: s.name,
          email: s.email,
          phone: s.phone
        }))
      });
      // Check for duplicate NAME (case-insensitive, trimmed)
      const duplicateName = existingStudents.find(s => s.name.toLowerCase().trim() === normalizedName);
      if (duplicateName) {
        console.log('[DUPLICATE CHECK] ❌ Duplicate NAME found:', {
          existingStudentId: duplicateName.id,
          existingStudentName: duplicateName.name,
          attemptedName: fullName
        });
        setNameError(`A student named "${fullName}" already exists. Please select the existing student instead of creating a duplicate.`);
        return false;
      }
      // Check for duplicate EMAIL (case-insensitive, trimmed)
      if (normalizedEmail) {
        const duplicateEmail = existingStudents.find(s => s.email && s.email.toLowerCase().trim() === normalizedEmail);
        if (duplicateEmail) {
          console.log('[DUPLICATE CHECK] ❌ Duplicate EMAIL found:', {
            existingStudentId: duplicateEmail.id,
            existingStudentName: duplicateEmail.name,
            existingEmail: duplicateEmail.email,
            attemptedEmail: email
          });
          setNameError(`A student with email "${email}" already exists (${duplicateEmail.name}). Please use the existing student record.`);
          return false;
        }
      }
      // Check for duplicate PHONE (normalized to digits only)
      if (normalizedPhone && normalizedPhone.length >= 8) {
        const duplicatePhone = existingStudents.find(s => {
          if (!s.phone) return false;
          const existingNormalizedPhone = normalizePhoneNumber(s.phone);
          return existingNormalizedPhone === normalizedPhone;
        });
        if (duplicatePhone) {
          console.log('[DUPLICATE CHECK] ❌ Duplicate PHONE found:', {
            existingStudentId: duplicatePhone.id,
            existingStudentName: duplicatePhone.name,
            existingPhone: duplicatePhone.phone,
            attemptedPhone: phone
          });
          setNameError(`A student with phone number "${phone}" already exists (${duplicatePhone.name}). Please use the existing student record.`);
          return false;
        }
      }
      console.log('[DUPLICATE CHECK] ✅ No duplicate found, safe to create');
      setNameError('');
      return true;
    } catch (error) {
      console.error('[DUPLICATE CHECK] Error checking for duplicates:', error);
      // On error, allow creation (fail open to not block legitimate adds)
      return true;
    }
  };
  // Handle email blur validation
  const handleEmailBlur = () => {
    if (formData.email) {
      validateEmail(formData.email);
    }
  };
  // Handle phone blur - format and validate
  const handlePhoneBlur = () => {
    if (formData.phone) {
      // Format the phone number on blur
      const formatted = formatPhoneNumber(formData.phone);
      setFormData(prev => ({
        ...prev,
        phone: formatted
      }));
      // Validate
      validatePhone(formatted);
    }
  };
  // Special handler for phone input with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const oldValue = formData.phone;
    const oldCursor = input.selectionStart || 0;
    const rawValue = input.value;
    // Format the phone number
    const formatted = formatPhoneNumber(rawValue);
    // Update state
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      if (phoneInputRef.current) {
        const newCursor = getFormattedCursorPosition(oldValue, formatted, oldCursor);
        phoneInputRef.current.setSelectionRange(newCursor, newCursor);
      }
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Clear previous errors
    setError('');
    setNameError('');
    // Validate required fields
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    // Validate email and phone before submitting
    const isEmailValid = validateEmail(formData.email);
    const isPhoneValid = validatePhone(formData.phone);
    const isGradeValid = validateGrade(formData.grade);
    if (!isEmailValid || !isPhoneValid || !isGradeValid) {
      setError('Please fix the validation errors before submitting');
      return;
    }
    // PHASE 3 ENHANCEMENT: Check for duplicate student by name, email, AND phone
    const isUnique = await checkDuplicateStudent(formData.firstName, formData.lastName, formData.email, formData.phone);
    if (!isUnique) {
      setError('Cannot create duplicate student');
      return;
    }
    setLoading(true);
    try {
      const studentId = `student-${Date.now()}`;
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      const normalizedPhone = normalizePhoneNumber(formData.phone);
      const newStudent = {
        studentId,
        id: studentId,
        name: fullName,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: normalizedPhone || '',
        instrument: formData.instrument.trim(),
        ...(formData.grade ? {
          grade: parseInt(formData.grade)
        } : {}),
        notes: formData.notes.trim(),
        profilePicture: '',
        avatar: '',
        status: 'active'
      };
      console.log('[ADD STUDENT] Creating new student:', {
        studentId,
        name: fullName,
        email: formData.email.trim()
      });
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      onSuccess(newStudent);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Error adding student:', err);
      setError('Failed to add student. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isOpen && student) {
      const nameParts = student.name ? student.name.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      setFormData({
        firstName,
        lastName,
        email: student.email || '',
        notes: '',
        phone: '',
        instrument: '',
        grade: ''
      });
      setError('');
      setEmailError('');
      setPhoneError('');
      setNameError('');
      setGradeError('');
    } else if (isOpen && !student) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        notes: '',
        phone: '',
        instrument: '',
        grade: ''
      });
      setError('');
      setEmailError('');
      setPhoneError('');
      setNameError('');
      setGradeError('');
    }
  }, [isOpen, student]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1886">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20" data-id="element-1887">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-1888">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1889"></div>
        </div>
        <div ref={modalRef} className={`relative inline-block w-full rounded-xl text-left overflow-hidden transform ${isDragging ? 'transition-none' : 'transition-all'} ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50 shadow-2xl' : 'bg-white border border-gray-200 shadow-xl'} max-md:h-screen max-md:max-w-none ${window.innerWidth >= 768 ? 'md:fixed' : ''} ${isDragging ? 'cursor-move' : ''}`} style={{
        width: 'clamp(360px, 90vw, 640px)',
        maxHeight: '88vh',
        ...(window.innerWidth >= 768 ? {
          top: `${modalPosition.top}px`,
          left: `${modalPosition.left}px`
        } : {}),
        userSelect: isDragging ? 'none' : 'auto',
        touchAction: 'pan-y',
        ...(darkMode && {
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
        })
      }} onClick={e => e.stopPropagation()} data-id="element-1890">
          {/* Header - now draggable */}
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} flex items-center justify-between flex-shrink-0 md:cursor-move`} onMouseDown={handleDragStart} title={window.innerWidth >= 768 ? 'Drag to move' : undefined} data-id="element-1891">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-1892">
              {student ? 'Add Student' : 'Add New Student'}
            </h3>
            <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-white hover:bg-navy-700/50' : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'} focus:outline-none rounded-lg p-1.5 transition-all`} data-id="element-1893">
              <XIcon className="h-5 w-5" data-id="element-1894" />
            </button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col" style={{
          maxHeight: 'calc(88vh - 80px)'
        }} data-id="element-1895">
            {/* Body - Scrollable */}
            <div className={`p-6 ${darkMode ? 'bg-transparent' : 'bg-white'} space-y-5 overflow-y-auto flex-grow`} data-id="element-1896">
              {/* Student Profile - only show if from messaging */}
              {student && <div className="flex items-center mb-4" data-id="element-1897">
                  <div className="h-16 w-16 rounded-full overflow-hidden mr-4 bg-gradient-to-br from-deep-pink to-burgundy flex-shrink-0" data-id="element-1898">
                    {student.avatar ? <img src={student.avatar} alt={student.name} className="h-full w-full object-cover" data-id="element-1899" /> : <div className="h-full w-full flex items-center justify-center text-white font-bold text-xl" data-id="element-1900">
                        {student.name.charAt(0).toUpperCase()}
                      </div>}
                  </div>
                  <div data-id="element-1901">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1902">
                      You're adding:
                    </p>
                    <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-1903">
                      {student.name}
                    </h4>
                  </div>
                </div>}
              {error && <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-500'} text-sm`} data-id="element-1904">
                  {error}
                </div>}

              {/* Duplicate student warning */}
              {nameError && <div className={`p-3 rounded-lg ${darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-50 text-yellow-700'} text-sm`} data-id="element-1905">
                  {nameError}
                </div>}

              {/* Success Toast */}
              {showToast && <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-50 text-green-600'} text-sm flex items-center`} data-id="element-1906">
                  <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-1907" />
                  Student added successfully.
                </div>}
              {/* Name fields - side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1908">
                <div data-id="element-1909">
                  <label htmlFor="firstName" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1910">
                    First Name *
                  </label>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className={`w-full rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} data-id="element-1911" />
                </div>
                <div data-id="element-1912">
                  <label htmlFor="lastName" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1913">
                    Last Name
                  </label>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={`w-full rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} data-id="element-1914" />
                </div>
              </div>
              <div data-id="element-1915">
                <label htmlFor="email" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1916">
                  Email *
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur} className={`w-full rounded-lg border ${emailError ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} placeholder="name@example.com" data-id="element-1917" />
                {emailError && <p className={`mt-1 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-1918">
                    {emailError}
                  </p>}
              </div>
              <div data-id="element-1919">
                <label htmlFor="phone" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1920">
                  Phone Number
                </label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onBlur={handlePhoneBlur} placeholder="+1 310 555 7890" className={`w-full rounded-lg border ${phoneError ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} data-id="element-1921" />
                <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1922">
                  Optional - Include country code, e.g. +1 310 555 7890
                </p>
                {phoneError && <p className={`mt-1 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-1923">
                    {phoneError}
                  </p>}
              </div>
              {/* Instrument and Grade - side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1924">
                <div data-id="element-1925">
                  <label htmlFor="instrument" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1926">
                    Instrument
                  </label>
                  <input type="text" id="instrument" name="instrument" value={formData.instrument} onChange={handleChange} className={`w-full rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} placeholder="e.g., Piano, Guitar, Violin" data-id="element-1927" />
                </div>
                <div data-id="element-1928">
                  <label htmlFor="grade" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1929">
                    Grade
                  </label>
                  <input type="number" id="grade" name="grade" value={formData.grade} onChange={handleChange} onBlur={() => validateGrade(formData.grade)} className={`w-full rounded-lg border ${gradeError ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} placeholder="e.g., 1, 2, 3" min="1" max="12" data-id="element-1930" />
                  {gradeError && <p className={`mt-1 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-1931">
                      {gradeError}
                    </p>}
                </div>
              </div>
              <div data-id="element-1932">
                <label htmlFor="notes" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1933">
                  Notes
                </label>
                <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} className={`w-full rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} placeholder="Add any additional notes about this student" data-id="element-1934"></textarea>
              </div>
            </div>
            {/* Footer - Fixed */}
            <div className={`px-6 py-4 border-t ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} flex justify-end space-x-3 flex-shrink-0`} data-id="element-1935">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`} data-id="element-1936">
                Cancel
              </button>
              <button type="submit" disabled={loading} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${loading ? 'opacity-50 cursor-not-allowed border-gray-600 text-gray-600' : darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-1937">
                {loading ? 'Adding...' : 'Add Student'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
};